import { useState, useEffect, useMemo, Fragment } from "react";
import { getCariListesi, runQuery, sendCariEkstreMail, getCariEkstrePdf } from "../services/api";

const DEFAULT_CARI_KOD = "00001";

function fmtNum(val) {
  if (val === null || val === undefined || isNaN(val)) return "—";
  const n = Number(val);
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
}
function fmtTarih(val) {
  if (!val) return "—";
  const [y, m, d] = String(val).slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

// SQL string literal içine güvenli şekilde koymak için tek tırnakları escape et
function sqlEscape(val) {
  return String(val).replace(/'/g, "''");
}

// Türkiye telefon numaralarını wa.me formatına (90XXXXXXXXXX) çevirir
function normalizeWhatsappTel(raw) {
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("0")) digits = "90" + digits.slice(1);
  else if (digits.length === 10) digits = "90" + digits;
  return digits;
}

function buildWhatsappMesaj(cariIsim, cariKod, baslangic, bitis, kpis) {
  const satirlar = [
    `*Cari Hesap Ekstresi*`,
    `${cariIsim} (${cariKod})`,
    "",
  ];
  if (baslangic || bitis) {
    satirlar.push(`Tarih Aralığı: ${baslangic ? fmtTarih(baslangic) : "—"} - ${bitis ? fmtTarih(bitis) : "—"}`);
  }
  satirlar.push(
    `Toplam Borç: ${fmtNum(kpis.toplamBorc)}`,
    `Toplam Alacak: ${fmtNum(kpis.toplamAlacak)}`,
    `Bakiye: ${fmtNum(kpis.bakiye)}`,
    "",
    "Detaylı ekstre PDF'ini az önce indirdiğim dosyadan ekliyorum 📎"
  );
  return satirlar.join("\n");
}

function buildEkstreSql(kod) {
  return `
SELECT
  TARIH, VADE_TARIHI, BELGE_NO, dbo.trk(ACIKLAMA) AS ACIKLAMA, BORC, ALACAK, HAREKET_TURU
FROM TBLCAHAR
WHERE CARI_KOD = '${sqlEscape(kod)}'
ORDER BY TARIH, INC_KEY_NUMBER
`.trim();
}

function buildFaturaDetaySql(belgeNoListesi) {
  const inList = belgeNoListesi.map((b) => `'${sqlEscape(b)}'`).join(",");
  return `
SELECT s.FISNO, s.STOK_KODU, dbo.trk(t.STOK_ADI) AS STOK_ADI, s.STHAR_GCMIK, s.STHAR_NF
FROM TBLSTHAR s
LEFT JOIN TBLSTSABIT t ON s.STOK_KODU = t.STOK_KODU
WHERE s.STHAR_FTIRSIP IN ('1','2') AND s.FISNO IN (${inList})
`.trim();
}

function buildHareketler(rows, baslangic, bitis) {
  const devirRows = [];
  const detayRows = [];
  for (const r of rows) {
    const tarihStr = r.TARIH ? String(r.TARIH).slice(0, 10) : null;
    if (bitis && tarihStr && tarihStr > bitis) continue; // bitiş tarihinden sonraki kayıtlar hiç getirilmez
    if (baslangic && tarihStr && tarihStr < baslangic) {
      devirRows.push(r);
    } else {
      detayRows.push(r);
    }
  }

  const devirBakiye = devirRows.reduce((s, r) => s + (Number(r.BORC) || 0) - (Number(r.ALACAK) || 0), 0);

  const result = [];
  let bakiye = devirBakiye;

  if (baslangic) {
    result.push({
      isDevir: true,
      tarih: fmtTarih(baslangic),
      vadeTarihi: "—",
      belgeNo: "—",
      aciklama: "Devir",
      hareketTuru: null,
      borc: 0,
      alacak: 0,
      bakiye: devirBakiye,
    });
  }

  detayRows.forEach((r) => {
    const borc = Number(r.BORC) || 0;
    const alacak = Number(r.ALACAK) || 0;
    bakiye += borc - alacak;
    result.push({
      tarih: fmtTarih(r.TARIH),
      vadeTarihi: fmtTarih(r.VADE_TARIHI),
      belgeNo: r.BELGE_NO || "—",
      aciklama: r.ACIKLAMA || "—",
      hareketTuru: r.HAREKET_TURU,
      borc,
      alacak,
      bakiye,
    });
  });

  return result;
}

function KpiCard({ label, value, color, icon }) {
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: "16px 18px", borderTop: `3px solid ${color}`, flex: 1, minWidth: 160 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      </div>
      <div style={{ color: "var(--text-heading)", fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export default function CariEkstre() {
  const [cariler, setCariler] = useState([]);
  const [loadingCariler, setLoadingCariler] = useState(true);
  const [cariSearch, setCariSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mailModal, setMailModal] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailCc, setMailCc] = useState("finans@emkyazilim.com");
  const [mailBaslangic, setMailBaslangic] = useState("");
  const [mailBitis, setMailBitis] = useState("");
  const [mailFaturaDetay, setMailFaturaDetay] = useState(false);
  const [mailSending, setMailSending] = useState(false);
  const [mailMsg, setMailMsg] = useState(null);

  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappTel, setWhatsappTel] = useState("");
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappError, setWhatsappError] = useState(null);

  const [pdfIndirLoading, setPdfIndirLoading] = useState(false);
  const [pdfIndirError, setPdfIndirError] = useState(null);

  const [showFaturaDetay, setShowFaturaDetay] = useState(false);
  const [faturaDetaylari, setFaturaDetaylari] = useState({});
  const [faturaDetayLoading, setFaturaDetayLoading] = useState(false);

  const [ekstreBaslangic, setEkstreBaslangic] = useState("2026-01-01");
  const [ekstreBitis, setEkstreBitis] = useState("2026-12-31");

  useEffect(() => {
    setLoadingCariler(true);
    getCariListesi()
      .then((r) => {
        setCariler(r.data);
        const varsayilan = r.data.find((c) => c.CARI_KOD === DEFAULT_CARI_KOD);
        if (varsayilan) setSelected(varsayilan);
      })
      .catch(() => {})
      .finally(() => setLoadingCariler(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    setMailMsg(null);
    setShowFaturaDetay(false);
    setFaturaDetaylari({});
    runQuery(buildEkstreSql(selected.CARI_KOD))
      .then((r) => setRows(r.data.rows))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [selected]);

  useEffect(() => {
    if (!showFaturaDetay || !rows) return;
    const belgeNoListesi = [
      ...new Set(rows.filter((r) => r.HAREKET_TURU === "B" && r.BELGE_NO).map((r) => r.BELGE_NO)),
    ];
    if (belgeNoListesi.length === 0) return;
    setFaturaDetayLoading(true);
    runQuery(buildFaturaDetaySql(belgeNoListesi))
      .then((r) => {
        const map = {};
        r.data.rows.forEach((d) => {
          if (!map[d.FISNO]) map[d.FISNO] = [];
          map[d.FISNO].push(d);
        });
        setFaturaDetaylari(map);
      })
      .catch(() => {})
      .finally(() => setFaturaDetayLoading(false));
  }, [showFaturaDetay, rows]);

  function handleEkstreGonderAc() {
    setMailTo("");
    setMailCc("finans@emkyazilim.com");
    setMailBaslangic("2026-01-01");
    setMailBitis("2026-12-31");
    setMailFaturaDetay(false);
    setMailMsg(null);
    setMailModal(true);
  }

  async function handleEkstreGonder() {
    setMailSending(true);
    setMailMsg(null);
    try {
      const r = await sendCariEkstreMail(selected.CARI_KOD, selected.CARI_ISIM, mailTo.trim(), mailCc.trim(), mailBaslangic, mailBitis, mailFaturaDetay);
      setMailModal(false);
      setMailMsg({ text: r.data.mesaj, type: "success" });
    } catch (e) {
      setMailMsg({ text: e.response?.data?.detail || e.message, type: "error" });
    } finally {
      setMailSending(false);
      setTimeout(() => setMailMsg(null), 6000);
    }
  }

  function handleWhatsappGonderAc() {
    setWhatsappTel("");
    setWhatsappError(null);
    setWhatsappModal(true);
  }

  async function pdfIndir() {
    const r = await getCariEkstrePdf(selected.CARI_KOD, selected.CARI_ISIM, ekstreBaslangic, ekstreBitis, showFaturaDetay);
    const blobUrl = URL.createObjectURL(r.data);
    const today = new Date();
    const bugunStr = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `Cari Hesap Ekstresi - ${bugunStr}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  }

  async function handlePdfIndir() {
    setPdfIndirLoading(true);
    setPdfIndirError(null);
    try {
      await pdfIndir();
    } catch (e) {
      setPdfIndirError(e.response?.data?.detail || e.message);
      setTimeout(() => setPdfIndirError(null), 6000);
    } finally {
      setPdfIndirLoading(false);
    }
  }

  async function handleWhatsappGonder() {
    setWhatsappSending(true);
    setWhatsappError(null);
    try {
      await pdfIndir();
      const tel = normalizeWhatsappTel(whatsappTel);
      const mesaj = buildWhatsappMesaj(selected.CARI_ISIM, selected.CARI_KOD, ekstreBaslangic, ekstreBitis, kpis);
      window.open(`https://wa.me/${tel}?text=${encodeURIComponent(mesaj)}`, "_blank");
      setWhatsappModal(false);
    } catch (e) {
      setWhatsappError(e.response?.data?.detail || e.message);
    } finally {
      setWhatsappSending(false);
    }
  }

  const filteredCariler = useMemo(
    () =>
      cariler.filter((c) =>
        (c.CARI_ISIM || "").toLowerCase().includes(cariSearch.toLowerCase()) ||
        (c.CARI_KOD || "").toLowerCase().includes(cariSearch.toLowerCase())
      ),
    [cariler, cariSearch]
  );

  const hareketler = useMemo(
    () => (rows ? buildHareketler(rows, ekstreBaslangic, ekstreBitis) : []),
    [rows, ekstreBaslangic, ekstreBitis]
  );

  const kpis = useMemo(() => {
    const toplamBorc = hareketler.reduce((s, h) => s + h.borc, 0);
    const toplamAlacak = hareketler.reduce((s, h) => s + h.alacak, 0);
    const bakiye = hareketler.length ? hareketler[hareketler.length - 1].bakiye : 0;
    return { toplamBorc, toplamAlacak, bakiye };
  }, [hareketler]);

  return (
    <div>
      {/* Mail Modal */}
      {mailModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--bg-page)", border: "1px solid var(--accent-bg)", borderRadius: 12, width: 480, padding: 28, boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--accent-text)", marginBottom: 6 }}>📧 Cari Hesap Ekstresi Gönder</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>{selected?.CARI_ISIM}</div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Alıcı (To) *</div>
              <input
                autoFocus
                type="email"
                placeholder="ornek@firma.com"
                value={mailTo}
                onChange={(e) => setMailTo(e.target.value)}
                style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>CC (isteğe bağlı)</div>
              <input
                type="email"
                value={mailCc}
                onChange={(e) => setMailCc(e.target.value)}
                style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ marginBottom: 24, display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Başlangıç Tarihi</div>
                <input
                  type="date"
                  value={mailBaslangic}
                  onChange={(e) => setMailBaslangic(e.target.value)}
                  style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box", colorScheme: "dark" }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Bitiş Tarihi</div>
                <input
                  type="date"
                  value={mailBitis}
                  min={mailBaslangic || undefined}
                  onChange={(e) => setMailBitis(e.target.value)}
                  style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box", colorScheme: "dark" }}
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: -14, marginBottom: 16 }}>
              Boş bırakılırsa tüm hareketler gönderilir. Başlangıçtan önceki hareketler "Devir" olarak tek satırda özetlenir.
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={mailFaturaDetay}
                onChange={(e) => setMailFaturaDetay(e.target.checked)}
                style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#1d4ed8" }}
              />
              <span style={{ fontSize: 13, color: "var(--text-primary)" }}>Fatura Detaylı</span>
              <span style={{ fontSize: 11, color: "var(--text-faint)" }}>(faturalardaki stok kırılımını da ekler)</span>
            </label>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setMailModal(false)}
                disabled={mailSending}
                style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 13, cursor: "pointer" }}
              >
                İptal
              </button>
              <button
                disabled={!mailTo.trim() || mailSending}
                onClick={handleEkstreGonder}
                style={{
                  padding: "9px 20px", borderRadius: 7, border: "none",
                  background: !mailTo.trim() || mailSending ? "var(--border)" : "#1d4ed8",
                  color: !mailTo.trim() || mailSending ? "var(--text-muted)" : "#fff",
                  fontSize: 13, fontWeight: 600, cursor: !mailTo.trim() || mailSending ? "not-allowed" : "pointer",
                }}
              >
                {mailSending ? "⏳ Gönderiliyor..." : "📧 Gönder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {whatsappModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--bg-page)", border: "1px solid var(--accent-bg)", borderRadius: 12, width: 420, padding: 28, boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--accent-text)", marginBottom: 6 }}>📱 WhatsApp ile Gönder</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>{selected?.CARI_ISIM}</div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Telefon Numarası *</div>
              <input
                autoFocus
                type="tel"
                placeholder="05xx xxx xx xx"
                value={whatsappTel}
                onChange={(e) => setWhatsappTel(e.target.value)}
                style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: -12, marginBottom: 20 }}>
              Ekstre PDF'i indirilir ve WhatsApp özet mesajıyla açılır; PDF'i sohbete sürükleyip siz gönderirsiniz.
            </div>
            {whatsappError && (
              <div style={{ fontSize: 12, color: "#fca5a5", marginTop: -12, marginBottom: 16 }}>{whatsappError}</div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setWhatsappModal(false)}
                disabled={whatsappSending}
                style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 13, cursor: "pointer" }}
              >
                İptal
              </button>
              <button
                disabled={normalizeWhatsappTel(whatsappTel).length < 10 || whatsappSending}
                onClick={handleWhatsappGonder}
                style={{
                  padding: "9px 20px", borderRadius: 7, border: "none",
                  background: normalizeWhatsappTel(whatsappTel).length < 10 || whatsappSending ? "var(--border)" : "#25D366",
                  color: normalizeWhatsappTel(whatsappTel).length < 10 || whatsappSending ? "var(--text-muted)" : "#fff",
                  fontSize: 13, fontWeight: 600, cursor: normalizeWhatsappTel(whatsappTel).length < 10 || whatsappSending ? "not-allowed" : "pointer",
                }}
              >
                {whatsappSending ? "⏳ Hazırlanıyor..." : "📱 WhatsApp'ta Aç"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cari seçici — üstte yatay açılır liste */}
      <div style={{ position: "relative", maxWidth: 440, marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Cari</div>
        <input
          value={dropdownOpen ? cariSearch : (selected ? selected.CARI_ISIM : "")}
          onChange={(e) => setCariSearch(e.target.value)}
          onFocus={() => { setDropdownOpen(true); setCariSearch(""); }}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
          placeholder="Cari ara veya seç..."
          style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
        />
        {dropdownOpen && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, maxHeight: 320, overflowY: "auto", zIndex: 30, boxShadow: "0 12px 28px rgba(0,0,0,0.35)" }}>
            {loadingCariler && <div style={{ color: "var(--text-faint)", fontSize: 13, padding: "10px 12px" }}>Yükleniyor...</div>}
            {!loadingCariler && filteredCariler.length === 0 && (
              <div style={{ color: "var(--text-faint)", fontSize: 13, padding: "10px 12px" }}>Cari bulunamadı</div>
            )}
            {filteredCariler.map((c) => {
              const isSelected = selected?.CARI_KOD === c.CARI_KOD;
              return (
                <button
                  key={c.CARI_KOD}
                  onClick={() => { setSelected(c); setDropdownOpen(false); setCariSearch(""); }}
                  title={c.CARI_ISIM}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    width: "100%", textAlign: "left",
                    padding: "9px 12px", border: "none", borderBottom: "1px solid var(--border-hairline)",
                    background: isSelected ? "var(--accent-bg)" : "transparent",
                    color: isSelected ? "var(--accent-text)" : "var(--text-secondary)",
                    cursor: "pointer", fontSize: 13,
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.CARI_ISIM}</span>
                  <span style={{ fontSize: 11, color: "var(--text-faint)", flexShrink: 0 }}>{c.CARI_KOD}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Ekstre */}
      <div>
        {!selected && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 400, color: "var(--text-ghost)", gap: 12 }}>
            <div style={{ fontSize: 48 }}>📄</div>
            <div style={{ fontSize: 16 }}>Yukarıdan bir cari seçin</div>
          </div>
        )}

        {selected && (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-heading)" }}>{selected.CARI_ISIM}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{selected.CARI_KOD} · {hareketler.length} hareket</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                {mailMsg && (
                  <div style={{ fontSize: 12, color: mailMsg.type === "error" ? "#fca5a5" : "#86efac", maxWidth: 320, textAlign: "right" }}>
                    {mailMsg.text}
                  </div>
                )}
                {pdfIndirError && (
                  <div style={{ fontSize: 12, color: "#fca5a5", maxWidth: 320, textAlign: "right" }}>
                    {pdfIndirError}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handlePdfIndir}
                    disabled={!rows || loading || pdfIndirLoading}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
                      background: "transparent", color: "var(--text-primary)", fontWeight: 600, fontSize: 13,
                      cursor: !rows || loading || pdfIndirLoading ? "not-allowed" : "pointer", opacity: !rows || loading || pdfIndirLoading ? 0.6 : 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pdfIndirLoading ? "⏳ Hazırlanıyor..." : "📄 PDF İndir"}
                  </button>
                  <button
                    onClick={handleEkstreGonderAc}
                    disabled={!rows || loading}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "9px 18px", borderRadius: 8, border: "1px solid var(--accent-bg)",
                      background: "#1d4ed8", color: "#fff", fontWeight: 600, fontSize: 13,
                      cursor: !rows || loading ? "not-allowed" : "pointer", opacity: !rows || loading ? 0.6 : 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    📧 Ekstre Gönder
                  </button>
                  <button
                    onClick={handleWhatsappGonderAc}
                    disabled={!rows || loading}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "9px 18px", borderRadius: 8, border: "1px solid #25D366",
                      background: "#25D366", color: "#fff", fontWeight: 600, fontSize: 13,
                      cursor: !rows || loading ? "not-allowed" : "pointer", opacity: !rows || loading ? 0.6 : 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    📱 WhatsApp
                  </button>
                </div>
              </div>
            </div>

            {loading && <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>⏳ Veri yükleniyor...</div>}
            {error && <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "12px 16px", color: "#fca5a5", fontSize: 13 }}>{error}</div>}

            {!loading && !error && rows && (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Başlangıç Tarihi</div>
                    <input
                      type="date"
                      value={ekstreBaslangic}
                      onChange={(e) => setEkstreBaslangic(e.target.value)}
                      style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "8px 11px", color: "var(--text-primary)", fontSize: 13, outline: "none", colorScheme: "dark" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Bitiş Tarihi</div>
                    <input
                      type="date"
                      value={ekstreBitis}
                      min={ekstreBaslangic || undefined}
                      onChange={(e) => setEkstreBitis(e.target.value)}
                      style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "8px 11px", color: "var(--text-primary)", fontSize: 13, outline: "none", colorScheme: "dark" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                  <KpiCard icon="📥" label="Toplam Borç" value={fmtNum(kpis.toplamBorc)} color="#ef4444" />
                  <KpiCard icon="📤" label="Toplam Alacak" value={fmtNum(kpis.toplamAlacak)} color="#22c55e" />
                  <KpiCard icon="⚖️" label="Bakiye" value={fmtNum(kpis.bakiye)} color={kpis.bakiye >= 0 ? "var(--accent)" : "#f59e0b"} />
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", width: "fit-content" }}>
                  <input
                    type="checkbox"
                    checked={showFaturaDetay}
                    onChange={(e) => setShowFaturaDetay(e.target.checked)}
                    style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#1d4ed8" }}
                  />
                  <span style={{ fontSize: 13, color: "var(--text-primary)" }}>Fatura Detaylı</span>
                  {faturaDetayLoading && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>⏳ yükleniyor...</span>}
                </label>

                {hareketler.length === 0 ? (
                  <div style={{ color: "var(--text-ghost)", fontSize: 14, textAlign: "center", padding: "40px 0" }}>Bu cari için hareket bulunamadı</div>
                ) : (
                  <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "var(--bg-card)" }}>
                          {["Tarih", "Vade Tarihi", "Belge No", "Açıklama", "Borç", "Alacak", "Bakiye"].map((c) => (
                            <th key={c} style={{ padding: "10px 14px", textAlign: c === "Tarih" || c === "Vade Tarihi" || c === "Belge No" || c === "Açıklama" ? "left" : "right", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {hareketler.map((h, i) => {
                          const detaylar = showFaturaDetay && h.hareketTuru === "B" ? faturaDetaylari[h.belgeNo] : null;
                          return (
                            <Fragment key={i}>
                              <tr style={{ background: h.isDevir ? "var(--border-hairline)" : i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-bg)")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = h.isDevir ? "var(--border-hairline)" : i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)")}
                              >
                                {h.isDevir ? (
                                  <>
                                    <td colSpan={4} style={{ padding: "8px 14px", color: "var(--text-secondary)", fontWeight: 700, borderBottom: "1px solid var(--border-hairline)" }}>Devir</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--text-ghost)", borderBottom: "1px solid var(--border-hairline)" }}>—</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--text-ghost)", borderBottom: "1px solid var(--border-hairline)" }}>—</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--accent-text)", fontWeight: 600, borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{fmtNum(h.bakiye)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td style={{ padding: "8px 14px", color: "var(--text-primary)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{h.tarih}</td>
                                    <td style={{ padding: "8px 14px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{h.vadeTarihi}</td>
                                    <td style={{ padding: "8px 14px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{h.belgeNo}</td>
                                    <td title={h.aciklama} style={{ padding: "8px 14px", color: "var(--text-muted)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap", fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{h.aciklama}</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: h.borc > 0 ? "#fca5a5" : "var(--border)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{h.borc > 0 ? fmtNum(h.borc) : "—"}</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: h.alacak > 0 ? "#86efac" : "var(--border)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{h.alacak > 0 ? fmtNum(h.alacak) : "—"}</td>
                                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--accent-text)", fontWeight: 600, borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>{fmtNum(h.bakiye)}</td>
                                  </>
                                )}
                              </tr>
                              {detaylar && detaylar.length > 0 && (
                                <tr style={{ background: "var(--bg-page)" }}>
                                  <td colSpan={7} style={{ padding: "6px 14px 12px 42px", borderBottom: "1px solid var(--border-hairline)" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                                      <thead>
                                        <tr>
                                          <th style={{ padding: "3px 10px", textAlign: "left", color: "var(--text-faint)", fontWeight: 600 }}>Stok Kodu</th>
                                          <th style={{ padding: "3px 10px", textAlign: "left", color: "var(--text-faint)", fontWeight: 600 }}>Stok Adı</th>
                                          <th style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-faint)", fontWeight: 600 }}>Miktar</th>
                                          <th style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-faint)", fontWeight: 600 }}>Birim Fiyat</th>
                                          <th style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-faint)", fontWeight: 600 }}>Tutar</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detaylar.map((d, di) => {
                                          const miktar = Number(d.STHAR_GCMIK) || 0;
                                          const fiyat = Number(d.STHAR_NF) || 0;
                                          return (
                                            <tr key={di}>
                                              <td style={{ padding: "3px 10px", color: "var(--text-muted)", fontFamily: "monospace", whiteSpace: "nowrap" }}>{d.STOK_KODU || "—"}</td>
                                              <td style={{ padding: "3px 10px", color: "var(--text-muted)" }}>{d.STOK_ADI || "—"}</td>
                                              <td style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{miktar.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                              <td style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtNum(fiyat)}</td>
                                              <td style={{ padding: "3px 10px", textAlign: "right", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtNum(miktar * fiyat)}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
